import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { useTierGate } from "../lib/PaywallContext";
import MapView from "../components/MapView";
import {
    MapPin, Calendar, Clock, Mountain, Heart, Bookmark, MessageCircle,
    Share2, Flag, Trash2, Pencil, ChevronLeft, ArrowRight, Compass,
    Cloud, Signal, Users, Dog, Baby, ShieldCheck, Lock, Sparkles,
    Backpack, Star, Award, X,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import ShareModal from "../components/ShareModal";
import ReviewsBlock from "../components/ReviewsBlock";
import { BeforeAfterGrid } from "../components/BeforeAfterUploader";
import { MarketplaceGearGrid } from "../components/MarketplaceGearPicker";
import SaveOfflineButton from "../components/SaveOfflineButton";

export default function AdventureDetail() {
    const { id } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    const gate = useTierGate(user);
    const [adv, setAdv] = useState(null);
    const [comments, setComments] = useState([]);
    const [comment, setComment] = useState("");
    const [loading, setLoading] = useState(true);
    const [activePhoto, setActivePhoto] = useState(0);
    const [shareOpen, setShareOpen] = useState(false);
    const [nudgeDismissed, setNudgeDismissed] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const [a, c] = await Promise.all([
                    api.get(`/adventures/${id}`),
                    api.get(`/adventures/${id}/comments`),
                ]);
                if (!cancelled) {
                    setAdv(a.data);
                    setComments(c.data);
                    setActivePhoto(0);
                }
            } catch {
                toast.error("Adventure not found");
                navigate("/explore");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [id, navigate]);

    useEffect(() => {
        setNudgeDismissed(!!id && localStorage.getItem(`rwo_guide_nudge_${id}`) === "1");
    }, [id]);

    const toggleLike = async () => {
        if (!user) { navigate("/login"); return; }
        try {
            const { data } = await api.post(`/adventures/${id}/like`);
            setAdv((a) => ({ ...a, liked: data.liked, likes_count: a.likes_count + (data.liked ? 1 : -1) }));
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const toggleSave = async () => {
        if (!user) { navigate("/login"); return; }
        try {
            const { data } = await api.post(`/adventures/${id}/save`);
            setAdv((a) => ({ ...a, saved: data.saved, saves_count: a.saves_count + (data.saved ? 1 : -1) }));
            toast.success(data.saved ? "Saved to your adventures" : "Removed from saved");
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const handleShare = () => setShareOpen(true);
    const handleDelete = async () => {
        if (!window.confirm("Delete this adventure?")) return;
        try {
            await api.delete(`/adventures/${id}`);
            toast.success("Deleted");
            navigate("/feed");
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const submitComment = async (e) => {
        e.preventDefault();
        if (!user) { navigate("/login"); return; }
        if (!comment.trim()) return;
        // Proactive gate so free users see the paywall instantly instead of
        // waiting for the backend 403. The axios interceptor still catches
        // any tier errors the UI doesn't anticipate.
        if (!gate({ required_tier: "adventure", feature: "post comments" })) return;
        try {
            const { data } = await api.post(`/adventures/${id}/comments`, { text: comment });
            setComments((c) => [data, ...c]);
            setComment("");
            setAdv((a) => ({ ...a, comments_count: a.comments_count + 1 }));
        } catch (e) { toast.error(formatApiError(e)); }
    };

    if (loading || !adv) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="w-10 h-10 border-4 border-ember-orange border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    const photos = adv.photos || [];
    const hero = photos[activePhoto] || photos[0];
    const isOwner = user?.user_id === adv.author?.user_id;
    const mapCenter = adv.lat && adv.lng ? [adv.lat, adv.lng] : null;

    const dismissNudge = () => {
        if (adv?.id) localStorage.setItem(`rwo_guide_nudge_${adv.id}`, "1");
        setNudgeDismissed(true);
    };

    return (
        <div>
            {/* Hero image */}
            <div className="relative h-[42vh] lg:h-[60vh] bg-midnight-pine overflow-hidden">
                {hero && <img src={hero} alt={adv.title} className="w-full h-full object-cover" />}
                <div className="absolute inset-0 hero-vignette" />
                <button
                    onClick={() => navigate(-1)}
                    data-testid="adv-back-btn"
                    className="absolute top-4 left-4 inline-flex items-center gap-1 px-3 py-2 rounded-md bg-snowcap/90 backdrop-blur text-granite font-semibold text-sm hover:bg-snowcap transition-all"
                >
                    <ChevronLeft className="w-4 h-4" /> Back
                </button>
                <div className="absolute inset-x-0 bottom-0 p-6 lg:p-10">
                    <div className="max-w-5xl mx-auto">
                        <div className="flex gap-2 mb-3">
                            <span className="px-3 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider bg-ember-orange text-white">
                                {adv.activity}
                            </span>
                            <span className="px-3 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider bg-snowcap/90 text-midnight-pine backdrop-blur">
                                {adv.difficulty}
                            </span>
                        </div>
                        <h1 className="font-display font-black text-white text-3xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tighter max-w-4xl">
                            {adv.title}
                        </h1>
                    </div>
                </div>
            </div>

            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
                {/* Author + actions */}
                <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
                    <Link
                        to={`/profile/${adv.author?.user_id}`}
                        data-testid="adv-author-link"
                        className="flex items-center gap-3 group"
                    >
                        <div className="w-12 h-12 rounded-full overflow-hidden bg-trail-tan">
                            {adv.author?.avatar ? (
                                <img src={adv.author.avatar} alt={adv.author.name} className="w-full h-full object-cover" />
                            ) : (
                                <Mountain className="w-6 h-6 text-midnight-pine m-auto mt-3" />
                            )}
                        </div>
                        <div>
                            <div className="font-bold text-granite group-hover:text-ember-orange transition-colors">{adv.author?.name}</div>
                            <div className="text-xs text-slate-gray">Posted {formatDistanceToNow(new Date(adv.created_at), { addSuffix: true })}</div>
                        </div>
                    </Link>
                    <div className="flex flex-wrap items-center justify-start sm:justify-end gap-2 w-full sm:w-auto">
                        <ActionButton testId="adv-like-btn" active={adv.liked} onClick={toggleLike} icon={Heart} label={adv.likes_count} />
                        <ActionButton testId="adv-save-btn" active={adv.saved} onClick={toggleSave} icon={Bookmark} label={adv.saved ? "Saved" : "Save"} />
                        <ActionButton testId="adv-share-btn" onClick={handleShare} icon={Share2} label="Share" />
                        {isOwner && (
                            <>
                                <ActionButton
                                    testId="adv-edit-btn"
                                    onClick={() => navigate(`/adventures/${adv.id}/edit`)}
                                    icon={Pencil}
                                    label="Edit"
                                />
                                <ActionButton testId="adv-delete-btn" onClick={handleDelete} icon={Trash2} label="" danger />
                            </>
                        )}
                    </div>
                </div>

                {/* "Turn this log into a guide" nudge (owner-only) */}
                {isOwner && !nudgeDismissed && (
                    <GuideNudge adv={adv} navigate={navigate} onDismiss={dismissNudge} />
                )}

                {/* Explicit "Save for offline" */}
                <SaveOfflineButton adv={adv} />

                {/* Stats grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                    <StatCard icon={MapPin} label="Location" value={adv.location_name || adv.general_area || "—"} />
                    <StatCard icon={Compass} label="Distance" value={adv.distance_km ? `${adv.distance_km} km` : "—"} />
                    <StatCard icon={Clock} label="Duration" value={adv.duration_hours ? `${adv.duration_hours} hrs` : "—"} />
                    <StatCard icon={Calendar} label="Date" value={adv.date ? new Date(adv.date).toLocaleDateString() : "—"} />
                </div>

                {/* Verdict + summary + warnings */}
                <TripHero adv={adv} />

                {/* Trip details — conditions, gear, recommendations, safety */}
                <TripDetails adv={adv} />

                {/* Main grid */}
                <div className="grid lg:grid-cols-[1fr,320px] gap-8">
                    <div>
                        {/* Description */}
                        <div className="prose max-w-none mb-10">
                            <div className="text-xs font-bold uppercase tracking-[0.25em] text-ember-orange mb-3">Trip report</div>
                            <p className="text-lg text-granite leading-relaxed whitespace-pre-line">{adv.description}</p>
                        </div>

                        {/* Photo gallery */}
                        {photos.length > 1 && (
                            <div className="mb-10">
                                <div className="text-xs font-bold uppercase tracking-[0.25em] text-ember-orange mb-3">Photos</div>
                                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                                    {photos.map((p, i) => (
                                        <button
                                            key={i}
                                            onClick={() => setActivePhoto(i)}
                                            data-testid={`adv-photo-${i}`}
                                            className={`aspect-square rounded-md overflow-hidden border-2 transition-all ${
                                                i === activePhoto ? "border-ember-orange" : "border-transparent hover:border-ridge-line"
                                            }`}
                                        >
                                            <img src={p} alt="" className="w-full h-full object-cover" />
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Video clips */}
                        {adv.videos?.length > 0 && (
                            <div data-testid="adv-videos" className="mb-10">
                                <div className="text-xs font-bold uppercase tracking-[0.25em] text-ember-orange mb-3">Video clips</div>
                                <div className="grid sm:grid-cols-2 gap-3">
                                    {adv.videos.map((url, i) => (
                                        <video
                                            key={i}
                                            data-testid={`adv-video-${i}`}
                                            src={url}
                                            controls
                                            playsInline
                                            preload="metadata"
                                            className="w-full aspect-video rounded-md bg-midnight-pine border border-ridge-line"
                                        />
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Before / After */}
                        {adv.before_after?.length > 0 && (
                            <div data-testid="adv-before-after" className="mb-10">
                                <div className="text-xs font-bold uppercase tracking-[0.25em] text-ember-orange mb-3">Before / After</div>
                                <BeforeAfterGrid pairs={adv.before_after} />
                            </div>
                        )}

                        {/* Map screenshots */}
                        {adv.map_screenshots?.length > 0 && (
                            <div data-testid="adv-map-screenshots" className="mb-10">
                                <div className="text-xs font-bold uppercase tracking-[0.25em] text-ember-orange mb-3">Map screenshots</div>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                    {adv.map_screenshots.map((url, i) => (
                                        <a
                                            key={i}
                                            data-testid={`adv-map-shot-${i}`}
                                            href={url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="aspect-[4/3] rounded-md overflow-hidden border border-ridge-line hover:border-ember-orange transition-all"
                                        >
                                            <img src={url} alt="Map" className="w-full h-full object-cover" />
                                        </a>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Linked marketplace gear */}
                        {adv.linked_gear?.length > 0 && (
                            <div data-testid="adv-linked-gear" className="mb-10">
                                <div className="text-xs font-bold uppercase tracking-[0.25em] text-ember-orange mb-3">Gear used (from Marketplace)</div>
                                <MarketplaceGearGrid ids={adv.linked_gear} />
                            </div>
                        )}

                        {/* Map */}
                        {mapCenter && (
                            <div className="mb-10">
                                <div className="text-xs font-bold uppercase tracking-[0.25em] text-ember-orange mb-3">
                                    GPS route
                                </div>
                                <div className="h-[360px] rounded-lg overflow-hidden border border-ridge-line">
                                    <MapView
                                        center={mapCenter}
                                        zoom={11}
                                        markers={[{ position: mapCenter, label: adv.title }]}
                                        route={adv.route}
                                    />
                                </div>
                                {adv.route?.length > 0 && (
                                    <div className="mt-3 flex items-center gap-3">
                                        <a
                                            href={`${process.env.REACT_APP_BACKEND_URL}/api/adventures/${adv.id}/gpx`}
                                            data-testid="adv-gpx-download"
                                            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-midnight-pine text-white font-bold text-sm hover:bg-midnight-pine/90"
                                        >
                                            <ArrowRight className="w-4 h-4 rotate-90" />
                                            Download GPX
                                        </a>
                                        <span className="text-xs text-slate-gray">
                                            {adv.route.length} trackpoints
                                            {adv.hide_exact_location && user?.user_id !== adv.author?.user_id &&
                                                " (coordinates fuzzed for privacy)"}
                                        </span>
                                    </div>
                                )}
                                {user?.tier !== "premium" && (
                                    <div className="mt-3 p-3 rounded-md bg-trail-tan/50 border border-trail-tan text-sm text-granite flex items-center justify-between gap-4">
                                        <span>Premium unlocks full, unfuzzed GPS export even for private spots.</span>
                                        <Link
                                            to="/pricing"
                                            data-testid="adv-upgrade-btn"
                                            className="inline-flex items-center gap-1 text-ember-orange font-bold hover:underline"
                                        >
                                            Upgrade <ArrowRight className="w-3 h-3" />
                                        </Link>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Tags */}
                        {adv.tags?.length > 0 && (
                            <div className="mb-10 flex flex-wrap gap-2">
                                {adv.tags.map((t) => (
                                    <span
                                        key={t}
                                        className="px-3 py-1.5 rounded-full bg-trail-tan/60 text-midnight-pine text-xs font-semibold"
                                    >
                                        #{t}
                                    </span>
                                ))}
                            </div>
                        )}

                        {/* Comments */}
                        <div>
                            <div className="text-xs font-bold uppercase tracking-[0.25em] text-ember-orange mb-3">
                                {adv.comments_count} comments
                            </div>
                            <form onSubmit={submitComment} className="flex gap-2 mb-6">
                                <input
                                    data-testid="adv-comment-input"
                                    value={comment}
                                    onChange={(e) => setComment(e.target.value)}
                                    placeholder={user ? "Add a comment…" : "Log in to comment"}
                                    disabled={!user}
                                    className="flex-1 px-4 py-3 rounded-md bg-snowcap border border-ridge-line focus:border-ember-orange focus:outline-none focus:ring-2 focus:ring-ember-orange/20 disabled:opacity-50"
                                />
                                <button
                                    type="submit"
                                    data-testid="adv-comment-submit"
                                    disabled={!user || !comment.trim()}
                                    className="px-5 py-3 rounded-md bg-ember-orange text-white font-bold hover:bg-ember-dark active:scale-95 disabled:opacity-50 transition-all"
                                >
                                    Post
                                </button>
                            </form>
                            <div className="space-y-4">
                                {comments.length === 0 ? (
                                    <div className="text-center py-8 text-slate-gray text-sm">
                                        Be the first to comment.
                                    </div>
                                ) : (
                                    comments.map((c) => (
                                        <div key={c.id} className="flex gap-3">
                                            <Link to={`/profile/${c.author?.user_id}`} className="shrink-0">
                                                <div className="w-10 h-10 rounded-full overflow-hidden bg-trail-tan">
                                                    {c.author?.avatar ? (
                                                        <img src={c.author.avatar} alt={c.author.name} className="w-full h-full object-cover" />
                                                    ) : (
                                                        <Mountain className="w-5 h-5 text-midnight-pine m-auto mt-2.5" />
                                                    )}
                                                </div>
                                            </Link>
                                            <div className="flex-1 bg-snowcap border border-ridge-line rounded-lg px-4 py-3">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <Link to={`/profile/${c.author?.user_id}`} className="font-bold text-sm text-granite hover:text-ember-orange">
                                                        {c.author?.name}
                                                    </Link>
                                                    <span className="text-xs text-slate-gray">
                                                        {formatDistanceToNow(new Date(c.created_at), { addSuffix: true })}
                                                    </span>
                                                </div>
                                                <p className="text-sm text-granite">{c.text}</p>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Sidebar */}
                    <aside className="space-y-5">
                        <div className="bg-midnight-pine text-white rounded-lg p-6">
                            <div className="text-xs font-bold uppercase tracking-[0.25em] text-ember-orange mb-3">
                                Recreate this
                            </div>
                            <p className="text-white/80 text-sm mb-4 leading-relaxed">
                                Save this trip and follow the route with full GPS guidance.
                            </p>
                            <button
                                onClick={toggleSave}
                                data-testid="adv-sidebar-save-btn"
                                className="w-full py-2.5 rounded-md bg-ember-orange text-white font-bold text-sm hover:bg-ember-dark active:scale-95 transition-all"
                            >
                                {adv.saved ? "Saved ✓" : "Save adventure"}
                            </button>
                        </div>

                        <div className="bg-snowcap border border-ridge-line rounded-lg p-5">
                            <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-gray mb-3">
                                Engagement
                            </div>
                            <div className="space-y-2">
                                <EngagementRow icon={Heart} label="Likes" value={adv.likes_count} />
                                <EngagementRow icon={MessageCircle} label="Comments" value={adv.comments_count} />
                                <EngagementRow icon={Bookmark} label="Saves" value={adv.saves_count} />
                            </div>
                        </div>

                        <button
                            data-testid="adv-report-btn"
                            onClick={async () => {
                                if (!user) return navigate("/login");
                                try {
                                    await api.post("/reports", { target_type: "adventure", target_id: adv.id, reason: "Inappropriate content" });
                                    toast.success("Report submitted");
                                } catch (e) { toast.error(formatApiError(e)); }
                            }}
                            className="w-full text-left text-sm text-slate-gray hover:text-trail-red flex items-center gap-2 px-2 py-1"
                        >
                            <Flag className="w-4 h-4" /> Report this adventure
                        </button>
                    </aside>
                </div>

                {/* Reviews */}
                <div className="mt-10">
                    <ReviewsBlock subjectKind="adventure" subjectId={adv.id} title="What others said" />
                </div>
            </div>
            <ShareModal
                open={shareOpen}
                onOpenChange={setShareOpen}
                kind="adventure"
                id={adv.id}
                title={adv.title}
                byline={adv.author?.name ? `by ${adv.author.name}` : ""}
                preview={[
                    adv.activity,
                    adv.location_name,
                    adv.distance_km ? `${adv.distance_km} km` : null,
                    adv.difficulty,
                ]
                    .filter(Boolean)
                    .join(" · ")}
            />
        </div>
    );
}

function GuideNudge({ adv, navigate, onDismiss }) {
    const hasRoute = !!(
        adv.distance_km || adv.elevation_gain_m || adv.duration_hours ||
        adv.route_type || adv.start_point || adv.end_point || (adv.terrain?.length)
    );
    const hasGear = !!(
        (adv.gear_used?.length) || (adv.safety_gear?.length) ||
        (adv.must_haves?.length) || (adv.nice_to_haves?.length) || adv.vehicle_needed
    );
    const hasSafety = !!(
        adv.fire_restrictions || adv.wildlife_notes || adv.trail_closure_warning ||
        adv.required_licenses || adv.private_land_warning || adv.leave_no_trace
    );

    const missing = [
        !hasRoute && { key: "route", label: "Add route", icon: Compass, hint: "Distance, elevation, GPX" },
        !hasGear && { key: "gear", label: "Add gear", icon: Backpack, hint: "What to pack" },
        !hasSafety && { key: "safety", label: "Add safety", icon: ShieldCheck, hint: "Permits, hazards, LNT" },
    ].filter(Boolean);

    if (missing.length === 0) return null;

    return (
        <div
            data-testid="guide-nudge"
            className="relative mb-8 rounded-xl border border-ember-orange/30 bg-ember-orange/5 p-5"
        >
            <button
                data-testid="guide-nudge-dismiss"
                onClick={onDismiss}
                className="absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center text-slate-gray hover:bg-snowcap hover:text-granite transition-colors"
                aria-label="Dismiss"
            >
                <X className="w-4 h-4" />
            </button>
            <div className="flex items-start gap-3">
                <span className="w-9 h-9 rounded-lg bg-ember-orange text-white flex items-center justify-center shrink-0">
                    <Sparkles className="w-5 h-5" />
                </span>
                <div className="min-w-0">
                    <h3 className="font-display font-black text-lg text-midnight-pine leading-tight">
                        Nice log. Want to turn it into a guide?
                    </h3>
                    <p className="text-sm text-slate-gray mt-0.5">
                        Add a few details so others can actually follow in your footsteps. One tap each — totally optional.
                    </p>
                    <div className="flex flex-wrap gap-2 mt-4">
                        {missing.map((m) => {
                            const Icon = m.icon;
                            return (
                                <button
                                    key={m.key}
                                    data-testid={`guide-nudge-${m.key}`}
                                    onClick={() => navigate(`/adventures/${adv.id}/edit?focus=${m.key}`)}
                                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-midnight-pine text-white text-sm font-bold hover:bg-deep-evergreen transition-all"
                                >
                                    <Icon className="w-4 h-4" /> {m.label}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}


function StatCard({ icon: Icon, label, value }) {
    return (
        <div className="bg-snowcap border border-ridge-line rounded-lg p-4">
            <Icon className="w-4 h-4 text-ember-orange mb-2" />
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-gray mb-1">{label}</div>
            <div className="font-display font-bold text-midnight-pine truncate">{value}</div>
        </div>
    );
}

function EngagementRow({ icon: Icon, label, value }) {
    return (
        <div className="flex items-center justify-between">
            <span className="flex items-center gap-2 text-sm text-slate-gray"><Icon className="w-4 h-4" /> {label}</span>
            <span className="font-display font-bold text-midnight-pine">{value}</span>
        </div>
    );
}

function ActionButton({ icon: Icon, label, onClick, active, danger, testId }) {
    return (
        <button
            onClick={onClick}
            data-testid={testId}
            className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-md border text-sm font-semibold active:scale-95 transition-all ${
                danger
                    ? "border-trail-red/30 text-trail-red hover:bg-trail-red hover:text-white"
                    : active
                    ? "bg-ember-orange border-ember-orange text-white"
                    : "bg-snowcap border-ridge-line text-granite hover:border-midnight-pine"
            }`}
        >
            <Icon className={`w-4 h-4 ${active ? "fill-white" : ""}`} /> {label}
        </button>
    );
}

const WORTH_IT_LABEL = {
    worth_it: { label: "Worth the drive", emoji: "🏔" },
    maybe: { label: "Maybe", emoji: "🤔" },
    lesson_learned: { label: "Lesson learned", emoji: "🪦" },
};

function TripHero({ adv }) {
    const wi = adv.worth_it_badge ? WORTH_IT_LABEL[adv.worth_it_badge] : null;
    const showWarnings =
        adv.location_fuzzed || adv.sensitive_location || adv.private_land_warning ||
        adv.trail_closure_warning || adv.fire_restrictions;
    if (!adv.summary && !wi && !showWarnings) return null;
    return (
        <div className="space-y-3 mb-8" data-testid="trip-hero">
            {adv.location_fuzzed && (
                <div
                    data-testid="warning-fuzzed"
                    className="flex items-start gap-3 p-3 rounded-md bg-ember-orange/10 border border-ember-orange/30 text-sm text-granite"
                >
                    <Lock className="w-4 h-4 text-ember-orange shrink-0 mt-0.5" />
                    <span>
                        <strong className="font-bold">Sensitive location:</strong>{" "}
                        the author has hidden the exact spot — coordinates and route shown are approximate.
                    </span>
                </div>
            )}
            {adv.trail_closure_warning && (
                <div className="flex items-start gap-3 p-3 rounded-md bg-trail-red/10 border border-trail-red/30 text-sm text-granite">
                    <ShieldCheck className="w-4 h-4 text-trail-red shrink-0 mt-0.5" />
                    <span><strong className="font-bold">Trail closure / hazard:</strong> {adv.trail_closure_warning}</span>
                </div>
            )}
            {adv.fire_restrictions && (
                <div className="flex items-start gap-3 p-3 rounded-md bg-trail-red/10 border border-trail-red/30 text-sm text-granite">
                    <ShieldCheck className="w-4 h-4 text-trail-red shrink-0 mt-0.5" />
                    <span><strong className="font-bold">Fire restrictions:</strong> {adv.fire_restrictions}</span>
                </div>
            )}
            {adv.private_land_warning && (
                <div className="flex items-start gap-3 p-3 rounded-md bg-trail-tan/50 border border-trail-tan text-sm text-granite">
                    <ShieldCheck className="w-4 h-4 text-midnight-pine shrink-0 mt-0.5" />
                    <span>This route may cross private land — verify access before going.</span>
                </div>
            )}
            <div className="flex flex-wrap items-center gap-3">
                {wi && (
                    <span
                        data-testid={`worthit-badge-${adv.worth_it_badge}`}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-midnight-pine text-white text-sm font-bold"
                    >
                        <span className="text-lg">{wi.emoji}</span> {wi.label}
                    </span>
                )}
                {adv.leave_no_trace && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-moss/15 text-moss text-xs font-bold uppercase tracking-widest">
                        <Sparkles className="w-3.5 h-3.5" /> Leave No Trace
                    </span>
                )}
                {adv.would_recommend === "yes" && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-ember-orange/15 text-ember-orange text-xs font-bold uppercase tracking-widest">
                        <Award className="w-3.5 h-3.5" /> Recommended
                    </span>
                )}
            </div>
            {adv.summary && (
                <p className="text-lg text-granite leading-relaxed font-medium border-l-4 border-ember-orange pl-4 italic">
                    {adv.summary}
                </p>
            )}
        </div>
    );
}

function TripDetails({ adv }) {
    const conditions = [
        { icon: Cloud, label: "Weather", value: adv.weather },
        { icon: Signal, label: "Cell service", value: adv.cell_service },
        { icon: Users, label: "Crowds", value: adv.crowds },
        { icon: Mountain, label: "Trail condition", value: adv.trail_condition },
        { icon: Compass, label: "Route type", value: adv.route_type ? adv.route_type.replace(/_/g, " ") : null },
        { icon: Mountain, label: "Elevation gain", value: adv.elevation_gain_m ? `${adv.elevation_gain_m} m` : null },
    ].filter((c) => c.value);

    const yesno = [
        { icon: Lock, label: "Permit required", value: adv.permit_required },
        { icon: ShieldCheck, label: "Fees required", value: adv.fees_required },
        { icon: Baby, label: "Kid friendly", value: adv.kid_friendly },
        { icon: Dog, label: "Dog friendly", value: adv.dog_friendly },
    ];

    const showConditions = conditions.length > 0 || yesno.some((y) => y.value);
    const showGear = (adv.gear_used?.length || 0) + (adv.must_haves?.length || 0) + (adv.safety_gear?.length || 0) + (adv.nice_to_haves?.length || 0) > 0 || adv.vehicle_needed;
    const showRecos = adv.best_for?.length > 0 || adv.tips_for_others || adv.ratings;
    const ratings = adv.ratings || {};
    const ratingEntries = Object.entries(ratings).filter(([, v]) => v > 0);

    if (!showConditions && !showGear && !showRecos) return null;

    return (
        <div className="space-y-6 mb-10" data-testid="trip-details">
            {showConditions && (
                <Panel icon={Cloud} title="Conditions">
                    {conditions.length > 0 && (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
                            {conditions.map((c) => (
                                <div key={c.label} className="flex items-start gap-2">
                                    <c.icon className="w-4 h-4 text-ember-orange shrink-0 mt-0.5" />
                                    <div>
                                        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-gray">{c.label}</div>
                                        <div className="text-sm font-bold text-midnight-pine capitalize">{c.value}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    <div className="flex flex-wrap gap-2 mt-2">
                        {yesno.map((y) => (
                            <span
                                key={y.label}
                                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${
                                    y.value
                                        ? "bg-moss/15 text-moss"
                                        : "bg-alpine-mist text-slate-gray"
                                }`}
                            >
                                <y.icon className="w-3.5 h-3.5" /> {y.label}: {y.value ? "Yes" : "No"}
                            </span>
                        ))}
                    </div>
                </Panel>
            )}

            {showGear && (
                <Panel icon={Backpack} title="Gear & prep">
                    {adv.must_haves?.length > 0 && (
                        <ChipList label="Must-haves" items={adv.must_haves} accent />
                    )}
                    {adv.gear_used?.length > 0 && <ChipList label="Gear used" items={adv.gear_used} />}
                    {adv.safety_gear?.length > 0 && <ChipList label="Safety gear" items={adv.safety_gear} accent />}
                    {adv.nice_to_haves?.length > 0 && <ChipList label="Nice-to-haves" items={adv.nice_to_haves} />}
                    {adv.vehicle_needed && (
                        <div className="text-sm text-granite mt-2">
                            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-gray mr-2">Vehicle</span>
                            <span className="font-bold text-midnight-pine">{adv.vehicle_needed}</span>
                        </div>
                    )}
                    {adv.wildlife_notes && (
                        <p className="text-sm text-granite mt-3"><strong className="font-bold">Wildlife: </strong>{adv.wildlife_notes}</p>
                    )}
                    {adv.required_licenses && (
                        <p className="text-sm text-granite mt-1"><strong className="font-bold">Licenses / permits: </strong>{adv.required_licenses}</p>
                    )}
                </Panel>
            )}

            {showRecos && (
                <Panel icon={Award} title="Recommendations">
                    {adv.best_for?.length > 0 && (
                        <div className="mb-3">
                            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-gray mb-2">Best for</div>
                            <div className="flex flex-wrap gap-1.5">
                                {adv.best_for.map((b) => (
                                    <span key={b} className="px-3 py-1 rounded-full bg-ember-orange/15 text-ember-orange text-xs font-bold capitalize">
                                        {b}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                    {adv.tips_for_others && (
                        <div className="mb-3">
                            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-gray mb-1">Tips for others</div>
                            <p className="text-sm text-granite leading-relaxed whitespace-pre-line">{adv.tips_for_others}</p>
                        </div>
                    )}
                    {ratingEntries.length > 0 && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3 pt-3 border-t border-ridge-line">
                            {ratingEntries.map(([k, v]) => (
                                <div key={k} className="flex items-center justify-between text-sm">
                                    <span className="text-granite capitalize">{k.replace(/_/g, " ")}</span>
                                    <span className="flex items-center gap-0.5">
                                        {[1, 2, 3, 4, 5].map((n) => (
                                            <Star key={n} className={`w-3.5 h-3.5 ${n <= v ? "fill-ember-orange text-ember-orange" : "text-ridge-line"}`} />
                                        ))}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </Panel>
            )}
        </div>
    );
}

function Panel({ icon: Icon, title, children }) {
    return (
        <section className="bg-snowcap border border-ridge-line rounded-lg p-5">
            <header className="flex items-center gap-2 mb-3">
                <Icon className="w-4 h-4 text-ember-orange" />
                <h3 className="font-display font-bold text-base text-midnight-pine">{title}</h3>
            </header>
            {children}
        </section>
    );
}

function ChipList({ label, items, accent }) {
    return (
        <div className="mb-2">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-gray mb-1.5">{label}</div>
            <div className="flex flex-wrap gap-1.5">
                {items.map((t) => (
                    <span
                        key={t}
                        className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                            accent
                                ? "bg-ember-orange/15 text-ember-orange"
                                : "bg-trail-tan/60 text-midnight-pine"
                        }`}
                    >
                        {t}
                    </span>
                ))}
            </div>
        </div>
    );
}

